import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  BarChart3,
  Code2,
  Download,
  Eye,
  ImageUp,
  MailCheck,
  RefreshCw,
  Save,
  Send,
  Settings,
  Upload,
  UserPlus,
  Users,
  Wand2,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const DEFAULT_SETTINGS = {
  productUrls: ['https://integratedtech.ca'],
  productContext: 'Integra AI builds AI-powered SaaS and web tools for modern teams.',
  fromName: 'Integra AI',
  replyTo: 'hello@integratedtech.ca'
};

const STARTER_HTML = `<!doctype html>
<html>
  <body style="margin:0;background:#1a1a1a;font-family:Arial,Helvetica,sans-serif;color:#ffffff;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#1a1a1a;">
      <tr>
        <td align="center" style="padding:40px 20px;">
          <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:100%;background:#242424;border-radius:8px;">
            <tr>
              <td style="padding:36px;">
                <h1 style="margin:0 0 12px;font-size:30px;line-height:1.2;color:#ffffff;">Integra AI</h1>
                <p style="margin:0 0 24px;color:#d4d4d4;font-size:16px;line-height:1.6;">Built for the road ahead.</p>
                <a href="https://integratedtech.ca" style="display:inline-block;background:#E8440A;color:#ffffff;text-decoration:none;padding:13px 18px;border-radius:6px;font-weight:bold;">Explore Integra AI</a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;

const endpoint = (BACKEND_URL, path) => `${BACKEND_URL}/api/admin/email-marketing${path}`;

const splitCsvLine = (line) => {
  const values = [];
  let current = '';
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];
    if (char === '"' && quoted && next === '"') {
      current += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === ',' && !quoted) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
};

const logsToCsv = (logs) => {
  const rows = [
    ['Timestamp', 'To', 'Recipient Name', 'Subject', 'Status', 'Error Message'],
    ...logs.map((log) => [
      log.timestamp,
      log.recipientEmail,
      log.recipientName,
      log.subjectLine,
      log.status,
      log.errorMessage || ''
    ])
  ];

  return rows
    .map((row) => row.map((cell) => `"${String(cell || '').replaceAll('"', '""')}"`).join(','))
    .join('\n');
};

const SectionTitle = ({ title, description }) => (
  <div>
    <h3 className="text-lg font-semibold text-foreground">{title}</h3>
    {description ? <p className="text-sm text-muted-foreground mt-1">{description}</p> : null}
  </div>
);

const StatusBadge = ({ status }) => (
  <Badge variant={status === 'sent' ? 'default' : 'destructive'}>{status || 'unknown'}</Badge>
);

const EmailMarketing = ({ fetchWithAuth, BACKEND_URL }) => {
  const [summary, setSummary] = useState({ totalContacts: 0, sentToday: 0, sentTotal: 0 });
  const [contacts, setContacts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactFilters, setContactFilters] = useState({ search: '', tag: '' });
  const [logFilters, setLogFilters] = useState({ startDate: '', endDate: '', status: 'all', email: '' });
  const [newContact, setNewContact] = useState({ name: '', email: '', company: '', tags: '' });
  const [imageBase64, setImageBase64] = useState('');
  const [mediaType, setMediaType] = useState('image/png');
  const [html, setHtml] = useState(STARTER_HTML);
  const [subject, setSubject] = useState('See what Integra AI can do next');
  const [previewLog, setPreviewLog] = useState(null);
  const [busy, setBusy] = useState({ contacts: false, logs: false, generate: false, send: false, settings: false });

  const loadContacts = async () => {
    setBusy((current) => ({ ...current, contacts: true }));
    try {
      const res = await fetchWithAuth(endpoint(BACKEND_URL, '/contacts'));
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to load contacts');
      setContacts(data.contacts || []);
      setSummary((current) => ({ ...current, totalContacts: (data.contacts || []).length }));
    } catch (error) {
      toast.error(error.message || 'Failed to load contacts');
    } finally {
      setBusy((current) => ({ ...current, contacts: false }));
    }
  };

  const loadLogs = async () => {
    setBusy((current) => ({ ...current, logs: true }));
    try {
      const params = new URLSearchParams();
      Object.entries(logFilters).forEach(([key, value]) => {
        if (value && value !== 'all') params.set(key, value);
      });
      const res = await fetchWithAuth(`${endpoint(BACKEND_URL, '/logs')}?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to load send log');
      const rows = data.logs || [];
      setLogs(rows);
      setSummary((current) => ({
        ...current,
        sentTotal: rows.filter((row) => row.status === 'sent').length,
        sentToday: rows.filter((row) => row.status === 'sent' && new Date(row.timestamp).toDateString() === new Date().toDateString()).length
      }));
    } catch (error) {
      toast.error(error.message || 'Failed to load send log');
    } finally {
      setBusy((current) => ({ ...current, logs: false }));
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetchWithAuth(endpoint(BACKEND_URL, '/settings'));
      if (!res.ok) return;
      const data = await res.json();
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    } catch {
      setSettings(DEFAULT_SETTINGS);
    }
  };

  useEffect(() => {
    loadContacts();
    loadLogs();
    loadSettings();
  }, []);

  const tags = useMemo(() => {
    return Array.from(new Set(contacts.flatMap((contact) => contact.tags || []))).sort();
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const term = contactFilters.search.toLowerCase();
    return contacts.filter((contact) => {
      const matchesSearch = [contact.name, contact.email, contact.company || ''].some((value) =>
        String(value).toLowerCase().includes(term)
      );
      const matchesTag = contactFilters.tag ? (contact.tags || []).includes(contactFilters.tag) : true;
      return matchesSearch && matchesTag;
    });
  }, [contacts, contactFilters]);

  const selectedContactRows = useMemo(
    () => contacts.filter((contact) => selectedContacts.includes(contact.id)),
    [contacts, selectedContacts]
  );

  const addContact = async (event) => {
    event.preventDefault();
    if (!newContact.name.trim() || !newContact.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const res = await fetchWithAuth(endpoint(BACKEND_URL, '/contacts'), {
        method: 'POST',
        body: JSON.stringify({
          ...newContact,
          tags: newContact.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to add contact');
      toast.success('Contact added');
      setNewContact({ name: '', email: '', company: '', tags: '' });
      loadContacts();
    } catch (error) {
      toast.error(error.message || 'Failed to add contact');
    }
  };

  const importContacts = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await fetchWithAuth(endpoint(BACKEND_URL, '/contacts/import'), {
          method: 'POST',
          body: JSON.stringify({ csv: String(reader.result || '') })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || data.error || 'Failed to import contacts');
        toast.success(`Imported ${data.imported || 0} contacts`);
        loadContacts();
      } catch (error) {
        toast.error(error.message || 'Failed to import contacts');
      }
    };
    reader.readAsText(file);
  };

  const parseLocalCsv = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = String(reader.result || '').split(/\r?\n/).filter(Boolean);
      if (rows.length < 2) return;
      const headers = splitCsvLine(rows[0]).map((header) => header.toLowerCase());
      toast.info(`CSV detected with columns: ${headers.join(', ')}`);
    };
    reader.readAsText(file);
  };

  const handleScreenshot = (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Upload a PNG or JPG screenshot');
      return;
    }
    setMediaType(file.type);
    const reader = new FileReader();
    reader.onload = () => setImageBase64(String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const generateCreative = async () => {
    if (!imageBase64) {
      toast.error('Upload a screenshot first');
      return;
    }
    const targetUrls = (settings.productUrls || []).map((url) => url.trim()).filter(Boolean);
    if (!targetUrls.length) {
      toast.error('Add at least one product URL in settings');
      return;
    }

    setBusy((current) => ({ ...current, generate: true }));
    try {
      const res = await fetchWithAuth(endpoint(BACKEND_URL, '/generate-creative'), {
        method: 'POST',
        body: JSON.stringify({
          imageBase64,
          mediaType,
          productContext: settings.productContext,
          targetUrls
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to generate creative');
      setHtml(data.html || '');
      toast.success('Creative generated');
    } catch (error) {
      toast.error(error.message || 'Failed to generate creative');
    } finally {
      setBusy((current) => ({ ...current, generate: false }));
    }
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    if (!selectedContactRows.length) {
      toast.error('Select at least one recipient');
      return;
    }
    if (!subject.trim() || !html.trim()) {
      toast.error('Subject and email HTML are required');
      return;
    }

    setBusy((current) => ({ ...current, send: true }));
    try {
      const res = await fetchWithAuth(endpoint(BACKEND_URL, '/send-email'), {
        method: 'POST',
        body: JSON.stringify({
          to: selectedContactRows.map((contact) => ({ name: contact.name, email: contact.email })),
          subject,
          html,
          fromName: settings.fromName,
          replyTo: settings.replyTo
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to send email');
      const failed = (data.results || []).filter((result) => result.status === 'failed').length;
      toast[failed ? 'error' : 'success'](failed ? `Sent with ${failed} failures. Check logs.` : 'Email sent and logged');
      loadLogs();
    } catch (error) {
      toast.error(error.message || 'Failed to send email');
    } finally {
      setBusy((current) => ({ ...current, send: false }));
    }
  };

  const saveSettings = async () => {
    setBusy((current) => ({ ...current, settings: true }));
    try {
      const res = await fetchWithAuth(endpoint(BACKEND_URL, '/settings'), {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to save settings');
      toast.success('Email marketing settings saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setBusy((current) => ({ ...current, settings: false }));
    }
  };

  const exportLogCsv = () => {
    const blob = new Blob([logsToCsv(logs)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'integra-ai-send-log.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-foreground">Email Marketing</h2>
        <p className="text-muted-foreground mt-2">Generate screenshot-inspired HTML emails, manage contacts, and send campaigns through protected backend APIs.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total contacts</p>
              <p className="text-3xl font-bold mt-2">{summary.totalContacts}</p>
            </div>
            <Users className="w-8 h-8 text-primary" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Sent today</p>
              <p className="text-3xl font-bold mt-2">{summary.sentToday}</p>
            </div>
            <Send className="w-8 h-8 text-primary" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Sent total</p>
              <p className="text-3xl font-bold mt-2">{summary.sentTotal}</p>
            </div>
            <MailCheck className="w-8 h-8 text-primary" />
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="creative" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto justify-start">
          <TabsTrigger value="creative"><Wand2 className="w-4 h-4 mr-2" />Creative</TabsTrigger>
          <TabsTrigger value="contacts"><Users className="w-4 h-4 mr-2" />Contacts</TabsTrigger>
          <TabsTrigger value="logs"><BarChart3 className="w-4 h-4 mr-2" />Logs</TabsTrigger>
          <TabsTrigger value="settings"><Settings className="w-4 h-4 mr-2" />Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="creative" className="space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Screenshot</CardTitle>
                  <CardDescription>Upload a PNG or JPG screenshot for Claude vision generation.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <label className="flex min-h-40 cursor-pointer flex-col items-center justify-center gap-3 rounded-md border border-dashed p-6 text-center hover:bg-muted">
                    <ImageUp className="w-8 h-8 text-primary" />
                    <span className="font-medium">Upload screenshot</span>
                    <span className="text-xs text-muted-foreground">Processed in browser as base64.</span>
                    <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(event) => handleScreenshot(event.target.files?.[0])} />
                  </label>
                  {imageBase64 ? <img src={imageBase64} alt="Uploaded screenshot" className="max-h-56 w-full object-contain rounded-md border" /> : null}
                  <Button className="w-full" onClick={generateCreative} disabled={busy.generate}>
                    {busy.generate ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    {busy.generate ? 'Generating' : 'Generate creative'}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recipients</CardTitle>
                  <CardDescription>Select contacts to receive this creative.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="max-h-72 overflow-auto rounded-md border">
                    {contacts.map((contact) => (
                      <label key={contact.id} className="flex cursor-pointer items-center gap-3 border-b p-3 last:border-b-0 hover:bg-muted">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={(event) => {
                            setSelectedContacts((current) =>
                              event.target.checked ? [...current, contact.id] : current.filter((id) => id !== contact.id)
                            );
                          }}
                        />
                        <span>
                          <span className="block text-sm font-medium">{contact.name}</span>
                          <span className="block text-xs text-muted-foreground">{contact.email}</span>
                        </span>
                      </label>
                    ))}
                    {!contacts.length ? <p className="p-4 text-sm text-muted-foreground">No contacts loaded.</p> : null}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Send</CardTitle>
                  <CardDescription>Send individually and log every attempt.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={sendEmail} className="space-y-4">
                    <div>
                      <Label>Subject line</Label>
                      <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>From: <span className="text-foreground">{settings.fromName}</span></p>
                      <p>Reply-to: <span className="text-foreground">{settings.replyTo}</span></p>
                      <p>Selected recipients: <span className="text-foreground">{selectedContactRows.length}</span></p>
                    </div>
                    <Button className="w-full" disabled={busy.send}>
                      {busy.send ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {busy.send ? 'Sending' : 'Send email'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>HTML Editor</CardTitle>
                  <CardDescription>Edit the generated inline-CSS email before sending.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea value={html} onChange={(event) => setHtml(event.target.value)} className="min-h-[360px] font-mono text-xs" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Code2 className="w-5 h-5" /> Live Preview</CardTitle>
                  <CardDescription>Rendered at the standard 600px email width.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-auto rounded-md border bg-muted p-4">
                    <iframe title="Generated email preview" srcDoc={html} className="mx-auto min-h-[720px] w-full max-w-[600px] border-0 bg-white" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle>Add or Import Contacts</CardTitle>
                <CardDescription>Contacts are stored by the backend in the configured Google Sheet.</CardDescription>
              </div>
              <label className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium hover:bg-accent">
                <Upload className="w-4 h-4" /> Import CSV
                <input type="file" accept=".csv,text/csv" className="hidden" onClick={(event) => { event.currentTarget.value = ''; }} onChange={(event) => { parseLocalCsv(event.target.files?.[0]); importContacts(event.target.files?.[0]); }} />
              </label>
            </CardHeader>
            <CardContent>
              <form onSubmit={addContact} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4">
                <div><Label>Name</Label><Input value={newContact.name} onChange={(event) => setNewContact({ ...newContact, name: event.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={newContact.email} onChange={(event) => setNewContact({ ...newContact, email: event.target.value })} /></div>
                <div><Label>Company</Label><Input value={newContact.company} onChange={(event) => setNewContact({ ...newContact, company: event.target.value })} /></div>
                <div><Label>Tags</Label><Input placeholder="trial, founder" value={newContact.tags} onChange={(event) => setNewContact({ ...newContact, tags: event.target.value })} /></div>
                <div className="flex items-end"><Button className="w-full"><UserPlus className="w-4 h-4" /> Add</Button></div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contact List</CardTitle>
              <CardDescription>Search by name, email, company, or filter by tag.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3">
                <Input placeholder="Search contacts" value={contactFilters.search} onChange={(event) => setContactFilters({ ...contactFilters, search: event.target.value })} />
                <select className="h-9 rounded-md border bg-background px-3 text-sm" value={contactFilters.tag} onChange={(event) => setContactFilters({ ...contactFilters, tag: event.target.value })}>
                  <option value="">All tags</option>
                  {tags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
                </select>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell className="font-medium">{contact.name}</TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.company}</TableCell>
                      <TableCell>{(contact.tags || []).join(', ')}</TableCell>
                      <TableCell>{contact.created_at ? new Date(contact.created_at).toLocaleDateString() : ''}</TableCell>
                    </TableRow>
                  ))}
                  {!busy.contacts && !filteredContacts.length ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No contacts found.</TableCell></TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle>Send Log</CardTitle>
                <CardDescription>Filter sent and failed email attempts from the backend log.</CardDescription>
              </div>
              <Button variant="outline" onClick={exportLogCsv}><Download className="w-4 h-4" /> Export CSV</Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_1fr_160px_1fr_auto] gap-4">
                <div><Label>Start date</Label><Input type="date" value={logFilters.startDate} onChange={(event) => setLogFilters({ ...logFilters, startDate: event.target.value })} /></div>
                <div><Label>End date</Label><Input type="date" value={logFilters.endDate} onChange={(event) => setLogFilters({ ...logFilters, endDate: event.target.value })} /></div>
                <div>
                  <Label>Status</Label>
                  <select className="h-9 w-full rounded-md border bg-background px-3 text-sm" value={logFilters.status} onChange={(event) => setLogFilters({ ...logFilters, status: event.target.value })}>
                    <option value="all">All</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div><Label>Recipient</Label><Input value={logFilters.email} onChange={(event) => setLogFilters({ ...logFilters, email: event.target.value })} /></div>
                <div className="flex items-end"><Button className="w-full" onClick={loadLogs} disabled={busy.logs}>Apply</Button></div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Preview</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap">{log.timestamp}</TableCell>
                      <TableCell>{log.recipientEmail}</TableCell>
                      <TableCell className="font-medium">{log.subjectLine}</TableCell>
                      <TableCell><StatusBadge status={log.status} /></TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={() => setPreviewLog(log)}><Eye className="w-4 h-4" /> Open</Button></TableCell>
                    </TableRow>
                  ))}
                  {!busy.logs && !logs.length ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No log rows found.</TableCell></TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle>Email Marketing Settings</CardTitle>
                <CardDescription>These values are sent to backend settings and used during creative generation and sending.</CardDescription>
              </div>
              <Button onClick={saveSettings} disabled={busy.settings}>
                <Save className="w-4 h-4" /> Save
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <SectionTitle title="Product URLs" description="Claude must use these real links in generated emails." />
                {(settings.productUrls || []).map((url, index) => (
                  <div key={`${url}-${index}`} className="flex gap-2">
                    <Input
                      value={url}
                      onChange={(event) => {
                        const next = [...(settings.productUrls || [])];
                        next[index] = event.target.value;
                        setSettings({ ...settings, productUrls: next });
                      }}
                    />
                    <Button variant="outline" size="icon" onClick={() => setSettings({ ...settings, productUrls: settings.productUrls.filter((_, itemIndex) => itemIndex !== index) })}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" onClick={() => setSettings({ ...settings, productUrls: [...(settings.productUrls || []), ''] })}>Add URL</Button>
              </div>
              <div>
                <Label>Product context</Label>
                <Textarea rows={5} value={settings.productContext} onChange={(event) => setSettings({ ...settings, productContext: event.target.value })} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>From name</Label><Input value={settings.fromName} onChange={(event) => setSettings({ ...settings, fromName: event.target.value })} /></div>
                <div><Label>Reply-to email</Label><Input type="email" value={settings.replyTo} onChange={(event) => setSettings({ ...settings, replyTo: event.target.value })} /></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!previewLog} onOpenChange={(open) => !open && setPreviewLog(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{previewLog?.subjectLine}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[75vh] overflow-auto rounded-md border bg-muted p-4">
            <iframe title="Send log email preview" srcDoc={previewLog?.emailBody || ''} className="mx-auto min-h-[720px] w-full max-w-[600px] border-0 bg-white" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailMarketing;
